import { useCallback, useState } from 'react';

export interface EnderecoPorCep {
  logradouro: string;
  bairro: string;
  cidade: string;
  uf: string;
}

/** `nao_encontrado`: o ViaCEP respondeu, o CEP nao existe. `falha_na_busca`: rede ou servico fora. */
export type ErroDeCep = 'nao_encontrado' | 'falha_na_busca';

const MENSAGEM_DO_ERRO: Record<ErroDeCep, string> = {
  nao_encontrado: 'CEP não encontrado. Preencha o endereço manualmente.',
  falha_na_busca: 'Não foi possível buscar o CEP agora. Preencha o endereço manualmente.',
};

type ResultadoDaBusca = { endereco: EnderecoPorCep } | { erro: ErroDeCep };

async function buscarCep(digitos: string): Promise<ResultadoDaBusca> {
  try {
    const resposta = await fetch(`https://viacep.com.br/ws/${digitos}/json/`);
    if (!resposta.ok) {
      return { erro: 'falha_na_busca' };
    }
    const dados = await resposta.json();
    if (dados.erro) {
      return { erro: 'nao_encontrado' };
    }
    return {
      endereco: {
        logradouro: dados.logradouro || '',
        bairro: dados.bairro || '',
        cidade: dados.localidade || '',
        uf: dados.uf || '',
      },
    };
  } catch {
    return { erro: 'falha_na_busca' };
  }
}

/**
 * Autocomplete de endereco a partir do CEP, via ViaCEP — servico publico, sem
 * relacao com a API do TEG.
 *
 * `aoEncontrarCep` chama de volta so quando o CEP resolve; quem usa decide se
 * preenche so os campos ainda vazios ou sobrescreve.
 *
 * `mensagemDeErro` EXISTE PORQUE O SILENCIO ANTERIOR ERA AMBIGUO: CEP digitado
 * errado e ViaCEP fora do ar terminavam do mesmo jeito — campos em branco, sem
 * pista nenhuma de por que o preenchimento automatico nao veio.
 */
export function useAutocompletarCep(aoEncontrarCep: (endereco: EnderecoPorCep) => void) {
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState<ErroDeCep | null>(null);

  const aoSairDoCampo = useCallback(
    async (cep: string) => {
      const digitos = cep.replace(/\D/g, '');
      if (digitos.length !== 8) {
        setErro(null);
        return;
      }
      setCarregando(true);
      setErro(null);
      try {
        const resultado = await buscarCep(digitos);
        if ('erro' in resultado) {
          setErro(resultado.erro);
        } else {
          aoEncontrarCep(resultado.endereco);
        }
      } finally {
        setCarregando(false);
      }
    },
    [aoEncontrarCep],
  );

  return {
    aoSairDoCampo,
    carregando,
    erro,
    mensagemDeErro: erro ? MENSAGEM_DO_ERRO[erro] : null,
  };
}
